import { useState } from "react"
import { Link, useLocalSearchParams, useRouter } from "expo-router"
import { Platform, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

import { List, Header, Text, MCIcon, ActivityIndicator, NotificationBadge, NotificationButton } from "@src/components"
import { ar } from "@src/i18n/ar"
import { formatDateForDisplay, priceFromDBFormat, sharedStyles, statusColors, trpc, RouterOutputs } from "@src/utils"

type Tab = "transactions" | "orders"
type CustomerListItem = RouterOutputs["customers"]["transactions"]["items"][number] | RouterOutputs["orders"]["list"]["items"][number]

export default function Customer() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params?.id, 10)
  const router = useRouter()
  const [tab, setTab] = useState<Tab>("transactions")

  const customer = trpc.customers.get.useQuery({ id })
  const trx = trpc.customers.transactions.useInfiniteQuery({ id }, { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined })
  const stats = trpc.customers.stats.useQuery({ id })
  const orders = trpc.orders.list.useInfiniteQuery({ customerId: id }, { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined })

  const allTransactions = trx.data?.pages.flatMap((page) => page.items) ?? []
  const allOrders = orders.data?.pages.flatMap((page) => page.items) ?? []

  if (customer.isLoading) {
    return (
      <>
        <Header title={ar.customerDetails} />
        <View className="flex-1 justify-center items-center">
          <ActivityIndicator />
        </View>
      </>
    )
  }

  if (!customer.data) {
    return (
      <>
        <Header title={ar.customerDetails} />
        <View className="flex-1 justify-center items-center">
          <Text>Error Loading Customer</Text>
        </View>
      </>
    )
  }

  const listHeader = (
    <>
      <View className="p-4 bg-white m-3 rounded border border-gray-200">
        <View className="flex-row justify-between items-start mb-4">
          <View>
            <Text className="text-gray-800 mb-1">{customer.data.id} #</Text>
            <Text className="text-2xl font-bold text-gray-800 mb-1">{customer.data.name}</Text>
            <Text className="text-gray-500">{customer.data.phoneNumbers?.[0]?.phone || "No Phone"}</Text>
            <NotificationBadge className="mt-2" reachable={customer.data.reachable} optedIn={customer.data.optedIn} />
          </View>
          <View className="flex-row gap-3 items-start">
            <MCIcon name="pencil" size={24} color="#6b7280" onPress={() => router.push(`/dashboard/customer/${id}/edit`)} />
            <NotificationButton customerId={id} size={24} />
          </View>
        </View>

        <View className="flex-row items-end gap-2 mb-4">
          <Text className="text-3xl font-bold text-blue-600">{priceFromDBFormat(customer.data.balance || 0)}</Text>
          <Text className="text-gray-500 mb-1.5">{ar.egp}</Text>
        </View>

        {/* Dates: created + last active */}
        <View className="flex-row items-center gap-4 mb-4">
          <View className="flex-row items-center gap-1">
            <MCIcon name="calendar-plus" size={14} color="#9ca3af" />
            <Text className="text-xs text-gray-400">{customer.data.createdAt ? formatDateForDisplay(customer.data.createdAt, "D MMM YYYY") : "-"}</Text>
          </View>
          <View className="flex-row items-center gap-1">
            <MCIcon name="clock-outline" size={14} color="#9ca3af" />
            <Text className="text-xs text-gray-400">{customer.data.lastActiveAt ? formatDateForDisplay(customer.data.lastActiveAt) : ar.never}</Text>
          </View>
        </View>

        <View className="flex-row justify-between mb-4">
          <View className="items-start">
            <Text className="text-gray-500 text-xs mb-1">{ar.totalOrders}</Text>
            <Text className="font-bold text-gray-800">{stats.data?.totalOrders ?? 0}</Text>
          </View>
          <View className="items-start">
            <Text className="text-gray-500 text-xs mb-1">{ar.totalSpent}</Text>
            <Text className="font-bold text-gray-800">
              {priceFromDBFormat(stats.data?.totalSpent ?? 0)} {ar.egp}
            </Text>
          </View>
          <View className="items-start">
            <Text className="text-gray-500 text-xs mb-1">{ar.lastOrder}</Text>
            <Text className="font-bold text-gray-800">{stats.data?.lastOrderAt ? formatDateForDisplay(stats.data.lastOrderAt) : ar.never}</Text>
          </View>
        </View>

        <View className="flex-row justify-between">
          <View />
          <Link href={`/dashboard/customer/${id}/new-transaction`} asChild>
            <Pressable className="px-2 self-center py-1 rounded border border-gray-500">
              <Text className="">{ar.addCustomerTransaction}</Text>
            </Pressable>
          </Link>

          <Link href={`/dashboard/customer/${id}/addresses`} asChild>
            <Pressable className="px-2 self-center py-1 rounded border border-gray-500">
              <Text className="">{ar.addresses}</Text>
            </Pressable>
          </Link>
        </View>
      </View>

      <View className="flex-row bg-gray-100 rounded p-1 mx-2 mb-1">
        {(["transactions", "orders"] as const).map((t) => (
          <Pressable key={t} onPress={() => setTab(t)} className={`flex-1 py-1 rounded items-center ${tab === t ? "bg-white" : "bg-transparent"}`}>
            <Text className={`font-semibold ${tab === t ? "text-blue-600" : "text-gray-500"}`}>{t === "transactions" ? ar.customerTransactions : ar.orders}</Text>
          </Pressable>
        ))}
      </View>
    </>
  )

  const isTransactions = tab === "transactions"
  const query = isTransactions ? trx : orders
  const data: CustomerListItem[] = isTransactions ? allTransactions : allOrders

  const right =
    Platform.OS === "web" ? (
      <Pressable
        hitSlop={12}
        onPress={() => Promise.all([customer.refetch(), query.refetch(), stats.refetch()])}
        disabled={customer.isPending}
        className="h-full justify-center items-center px-2"
      >
        <MCIcon name="refresh" size={22} color="#4b5563" />
      </Pressable>
    ) : undefined

  return (
    <>
      <Header title={ar.customerDetails} right={right} />
      <List
        data={data}
        contentContainerStyle={sharedStyles.contentContainer}
        keyExtractor={(item) => (isTransactions ? "t" : "o") + item.id}
        ListHeaderComponent={listHeader}
        onRefresh={() => Promise.all([customer.refetch(), query.refetch(), stats.refetch()])}
        onEndReached={() => {
          if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={<Text className="pt-4 text-center text-gray-400">{isTransactions ? ar.noCustomerTransactions : ar.noOrders}</Text>}
        ListFooterComponent={
          <>
            {query.isFetchingNextPage && <ActivityIndicator className="py-4" />}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
        renderItem={({ item }) => {
          if ("orderNumber" in item) {
            const statusColor = statusColors[item.status]
            return (
              <Link asChild href={`/dashboard/orders/${item.id}`}>
                <Pressable className="bg-white p-2 mx-3 mb-2 rounded border border-gray-100 flex-row justify-between items-center">
                  <View>
                    <Text className="font-semibold text-gray-800 mb-1">#{item.orderNumber}</Text>
                    <Text className="text-xs text-gray-600">{formatDateForDisplay(item.createdAt)}</Text>
                    <View className="mt-1 px-2 py-0.5 rounded self-start" style={{ backgroundColor: `${statusColor}20` }}>
                      <Text className="text-xs font-semibold" style={{ color: statusColor }}>
                        {ar[item.status]}
                      </Text>
                    </View>
                  </View>
                  <View className="items-end">
                    <Text className="font-bold text-gray-800">{priceFromDBFormat(item.total)}</Text>
                    <Text className="text-xs text-gray-400">{ar.egp}</Text>
                  </View>
                </Pressable>
              </Link>
            )
          }

          const isCredit = item.type === "PAYMENT" || item.type === "REFUND"
          return (
            <View className="bg-white p-2 mx-3 mb-2 rounded border border-gray-100 flex-row justify-between items-center">
              <View>
                <Text className="font-semibold text-gray-800 mb-1">{ar[item.type]}</Text>
                <Text className="text-xs text-gray-600">{formatDateForDisplay(item.date)}</Text>
                {item.note && <Text className="text-xs text-gray-500 mt-1">{item.note}</Text>}
                {item.receiptRef && (
                  <Link href={`/dashboard/receipt/hash/${item.receiptRef}`} asChild>
                    <Pressable className="pt-2 px-2">
                      <Text className="text-xs text-blue-600">{ar.receipt}</Text>
                    </Pressable>
                  </Link>
                )}
              </View>

              <View className="items-end">
                <Text className={`font-bold ${isCredit ? "text-green-600" : "text-red-600"}`}>
                  {isCredit ? "+" : "-"}
                  {priceFromDBFormat(item.amount)}
                </Text>
                <Text className="text-xs text-gray-400">{ar.egp}</Text>
              </View>
            </View>
          )
        }}
      />
    </>
  )
}
